import TemplateLayout from '@/components/layouts/TemplateLayout';
import StickyAppHeader from '@/components/molecules/sticky-app-header';
import { CustomPageProps } from '@/types/app';

import ClickLogger from '@/components/common/ClickLogger';
import { EWebTreatmentRequestLog } from '@/entities/diagnosis/libs';
import { useTelemedicineRequestViewModel } from '../hooks';
import {
  CertificationGuideStep,
  DiagnosisFormStep,
  DietGuideBottomSheet,
  DoctorFilterSelectionStep,
  MaleGuideBottomSheet,
  MaternityGuideBottomSheet,
  MentalHealthGuideBottomSheet,
  PaymentFormStep,
  SleepDisorderGuideBottomSheet,
  TelemedicineDoctorSelect,
} from '../ui';
import TelemedicineRequestLoader from './TelemedicineRequestLoader';

const TelemedicineRequestView = ({ client }: CustomPageProps) => {
  const viewModel = useTelemedicineRequestViewModel(client);

  if (!viewModel.isRequiredDataFetched) {
    return <TelemedicineRequestLoader />;
  }
  return (
    <TemplateLayout.Wrapper>
      <TemplateLayout.StickyHeader>
        <ClickLogger params={{ eventName: EWebTreatmentRequestLog.ClickCloseButton }}>
          <StickyAppHeader title={viewModel.getHeaderTitle()} onClickBackButton={viewModel.moveToBack} />
        </ClickLogger>
      </TemplateLayout.StickyHeader>
      <TemplateLayout.Content>
        {viewModel.shouldShowDiagnosisFormStep() && (
          <DiagnosisFormStep
            client={client}
            bookingSchedulesInfo={viewModel.bookingSchedulesInfo}
            treatmentRequest={viewModel.treatmentRequest}
            accordionRefs={viewModel.accordionRefs}
            selectedDayScheduleLength={viewModel.selectedDayScheduleLength}
            selectedDay={viewModel.selectedDay}
            ssnInputRef={viewModel.ssnInputRef}
            categoryName={viewModel.getCategoryName()}
            isPreSignedUrlsLoading={viewModel.isPreSignedUrlsLoading}
            detailSymptomInputRef={viewModel.detailSymptomInputRef}
            recentlyDetailSymptomsCount={viewModel.recentlyDetailSymptomsCount}
            isChronicDiseaseCategory={viewModel.isChronicDiseaseCategory}
            chronicDiseaseCategoryWarningMessages={viewModel.chronicDiseaseCategoryWarningMessages}
            chronicDiseaseCategoryPlaceholder={viewModel.chronicDiseaseCategoryPlaceholder}
            selectedTimeSlotTitle={viewModel.selectedTimeSlotTitle}
            isDoctorStepButtonDisabled={viewModel.isDoctorStepButtonDisabled}
            userProfile={viewModel.userProfiles.getUserProfileByTargetId(viewModel.userTargetId)}
            selectBookingTargetDate={viewModel.selectBookingTargetDate}
            selectBookingTimeSlot={viewModel.selectBookingTimeSlot}
            toggleAsapRequest={viewModel.toggleAsapRequest}
            selectQuickMode={viewModel.selectQuickMode}
            selectGeneralMode={viewModel.selectGeneralMode}
            updatePatientSsn={viewModel.updatePatientSsn}
            resetPatientSsn={viewModel.resetPatientSsn}
            updateSymptom={viewModel.updateSymptom}
            onHandleChangeSymptomImage={viewModel.onHandleChangeSymptomImage}
            removeSymptomImage={viewModel.removeSymptomImage}
            openDetailSymptomsLoadBottomSheet={viewModel.openDetailSymptomsLoadBottomSheet}
            requestCameraPermission={viewModel.requestCameraPermission}
            handleDiagnosisStepNext={viewModel.handleDiagnosisStepNext}
            diagnosisStepButtonText={viewModel.getDiagnosisStepButtonText()}
            isSymptomBottomSheetOpen={viewModel.isRadioBottomSheetOpen}
            recentlyDetailSymptoms={viewModel.recentlyDetailSymptoms}
            onCloseSymptomBottomSheet={viewModel.closeRadioBottomSheet}
            onSymptomSelect={viewModel.handleSymptomSelect}
          />
        )}
        {viewModel.shouldShowDoctorFilterStep() && (
          <DoctorFilterSelectionStep
            selectedFilters={viewModel.selectedDoctorFilters}
            onToggleFilter={viewModel.handleToggleDoctorFilter}
            onNext={viewModel.handleDoctorFilterSelectionNext}
          />
        )}
        {viewModel.shouldShowDoctorSelectStep() && (
          <TelemedicineDoctorSelect
            client={client}
            searchDoctors={viewModel.searchDoctors}
            treatmentRequest={viewModel.treatmentRequest}
            categoryName={viewModel.getCategoryName()}
            selectedFilters={viewModel.selectedDoctorFilters}
            isLoadingSearchDoctors={viewModel.isLoadingSearchDoctors}
            hasNextPageSearchDoctors={viewModel.hasNextPageSearchDoctors}
            isSearchDoctorSortFilterBottomSheetOpen={viewModel.isSearchDoctorSortFilterBottomSheetOpen}
            selectedSortKey={viewModel.treatmentRequest.getSortKey()}
            handleSearchDoctorSpecialistOnlySelect={viewModel.handleSearchDoctorSpecialistOnlySelect}
            handleSearchDoctorSortFilterSelect={viewModel.handleSearchDoctorSortFilterSelect}
            fetchNextPageSearchDoctors={viewModel.fetchNextPageSearchDoctors}
            openSearchDoctorSortFilterBottomSheet={viewModel.openSearchDoctorSortFilterBottomSheet}
            closeSearchDoctorSortFilterBottomSheet={viewModel.closeSearchDoctorSortFilterBottomSheet}
            onOpenDoctorDetailBottomSheet={viewModel.onOpenDoctorDetailBottomSheet}
            getDoctorDetailOpenCount={viewModel.getDoctorDetailOpenCount}
            isDoctorDetailBottomSheetOpen={!!viewModel.getDoctorIdByQueryString()}
            onCloseDoctorDetailBottomSheet={viewModel.onCloseDoctorDetailBottomSheet}
            handleSelectDoctor={viewModel.handleSelectDoctor}
            isEnabledScheduleAlertOpen={viewModel.isEnabledScheduleAlertOpen}
            selectedStartDisplayTimeSlot={viewModel.selectedStartDisplayTimeSlot}
            selectedDoctorDetail={viewModel.selectedDoctorDetail}
            onCloseIsEnabledScheduleAlert={viewModel.onCloseIsEnabledScheduleAlert}
          />
        )}
        {viewModel.shouldShowPaymentStep() && (
          <PaymentFormStep
            cardListWrapperRef={viewModel.cardListWrapperRef}
            isUserPaymentsInfoRefetching={viewModel.isUserPaymentsInfoRefetching}
            isUserPaymentsInfoFetched={viewModel.isUserPaymentsInfoFetched}
            point={viewModel.point}
            cardCompanyButton={viewModel.cardCompanyButton}
            treatmentRequest={viewModel.treatmentRequest}
            userPaymentsInfo={viewModel.userPaymentsInfo}
            requestAgreementList={viewModel.requestAgreementList}
            isRequestDiagnosisLoading={viewModel.isRequestDiagnosisLoading}
            bookingSchedulesInfo={viewModel.bookingSchedulesInfo}
            doctorPublicId={viewModel.doctorPublicId}
            user={viewModel.user}
            selectCard={viewModel.selectCard}
            selectCardCompanyButton={viewModel.selectCardCompanyButton}
            toggleRequestUsePoint={viewModel.toggleRequestUsePoint}
            openCardInstallmentListBottomSheet={viewModel.openCardInstallmentListBottomSheet}
            addCreditCard={viewModel.addCreditCard}
            addKakaopay={viewModel.addKakaopay}
            toggleTreatmentRequestAgreement={viewModel.toggleTreatmentRequestAgreement}
            toggleTreatmentRequestAllAgreement={viewModel.toggleTreatmentRequestAllAgreement}
            moveToAgreementLink={viewModel.moveToAgreementLink}
            onSubmitRequestDiagnosis={viewModel.onSubmitRequestDiagnosis}
            scrollToBottomAndCheckAllAgreement={viewModel.scrollToBottomAndCheckAllAgreement}
            isCardRegisterBottomSheetOpen={viewModel.getIsGeneralCardRegister()}
            onCloseCardRegisterBottomSheet={viewModel.onCloseRegisterCardBottomSheet}
            onSuccessCardRegister={viewModel.onSuccessCardRegister}
            isCardInstallmentBottomSheetOpen={viewModel.isCardInstallmentBottomSheetOpen}
            cardInstallments={viewModel.userPaymentsInfo.installments}
            onCardInstallmentSelect={viewModel.handleInstallmentSelect}
            onCloseCardInstallmentBottomSheet={viewModel.closeCardInstallmentBottomSheet}
          />
        )}
        {viewModel.shouldShowCertificationGuideStep() && (
          <CertificationGuideStep onCertificationSuccess={viewModel.onCertificationSuccess} />
        )}
      </TemplateLayout.Content>

      <MaternityGuideBottomSheet
        isOpen={viewModel.isMaternityGuideBottomSheetOpen}
        onClose={viewModel.closeMaternityGuideBottomSheet}
      />
      <MaleGuideBottomSheet
        isOpen={viewModel.isMaleGuideBottomSheetOpen}
        onClose={viewModel.closeMaleGuideBottomSheet}
      />
      <MentalHealthGuideBottomSheet
        isOpen={viewModel.isMentalHealthGuideBottomSheetOpen}
        onClose={viewModel.closeMentalHealthGuideBottomSheet}
      />
      <DietGuideBottomSheet
        isOpen={viewModel.isDietGuideBottomSheetOpen}
        onClose={viewModel.closeDietGuideBottomSheet}
      />
      <SleepDisorderGuideBottomSheet
        isOpen={viewModel.isSleepDisorderGuideBottomSheetOpen}
        onClose={viewModel.closeSleepDisorderGuideBottomSheet}
      />
    </TemplateLayout.Wrapper>
  );
};

export default TelemedicineRequestView;
